import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';

import '../contracts/SubaccountsLinks_CriarInput';
import '../contracts/SubaccountsLinks_RefundInput';
import '../contracts/SubaccountsLinks_RefundRecebimentoInput';
import '../contracts/SubaccountsLinks_AtivoResponse';
import '../contracts/SubaccountsLinks_CriarResponse';
import '../contracts/SubaccountsLinks_DesativarResponse';
import '../contracts/SubaccountsLinks_DetalheResponse';
import '../contracts/SubaccountsLinks_HistoricoResponse';
import '../contracts/SubaccountsLinks_ListaResponse';
import '../contracts/SubaccountsLinks_PixResponse';
import '../contracts/SubaccountsLinks_RefundRecebimentoResponse';
import '../contracts/SubaccountsLinks_RefundResponse';
import '../contracts/SubaccountsLinks_StatsResponse';
import '../contracts/SubaccountsLinks_SyncChargeResponse';
import '../contracts/SubaccountsLinks_SyncChargeInput';
import '../contracts/SubaccountsLinks_SyncPaymentLinkResponse';
import '../contracts/SubaccountsLinks_SyncPaymentLinkInput';
import '../contracts/SubaccountsLinks_TodosResponse';
import type { SubaccountsLinks_CriarInput } from '../contracts/SubaccountsLinks_CriarInput';
import type { SubaccountsLinks_RefundInput } from '../contracts/SubaccountsLinks_RefundInput';
import type { SubaccountsLinks_RefundRecebimentoInput } from '../contracts/SubaccountsLinks_RefundRecebimentoInput';
// Valid owner types - exported for external use
export const VALID_OWNERS = ['compromisso', 'royalty', 'invoice', 'subscription', 'order'];

export type PaymentLinkId = number | string;

export interface SyncSummary {
  charges_found?: number;
  charges_processed_ok?: number;
  charges_with_errors?: number;
  total_paid?: number;
  total_pending?: number;
}

export interface SyncResult {
  synced?: boolean;
  payment_link_id?: PaymentLinkId;
  provider_link_id?: string;
  previous_status?: string;
  current_status?: string;
  status_changed?: boolean;
  summary?: SyncSummary;
  charges?: unknown[];
  errors?: unknown[];
}

export interface PaymentLinkRow {
  id: PaymentLinkId;
  status?: string;
  url?: string;
  [key: string]: unknown;
}

// Status constants - exported for external use
export const STATUS = {
  ACTIVE: 'active',
  PAID: 'paid',
  PARTIALLY_PAID: 'partially_paid',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
  OVERDUE: 'overdue',
};

/**
 * Composable for managing Payment Links with polymorphic ownership
 *
 * ARCHITECTURE:
 * =============
 * Payment links support any owner type (compromisso, royalty, invoice, etc.)
 * using polymorphic ownership (owner + owner_id columns).
 *
 * Features:
 * - Create payment links for any owner entity
 * - Support PIX, Boleto, Credit Card payment methods
 * - Track link status and history timeline
 * - Auto-create recebimentos when payments are confirmed via webhook
 *
 * @see claudedocs/compromissos-payment-links-design.md
 */
export function usePaymentLinks() {
  const isLoading = ref(false);
  const error = ref(null);

  // ===========================================================================
  // POLYMORPHIC CRUD OPERATIONS
  // ===========================================================================

  /**
   * Create a new payment link for any owner type
   *
   * @param {Object} data - Payment link data
   * @param {string} data.owner - Owner type ('compromisso', 'royalty', 'invoice', etc.)
   * @param {number} data.owner_id - Owner entity ID
   * @param {number} data.value - Payment value (optional, uses owner's value if not provided)
   * @param {number} data.due_days - Days until due date (default: 10)
   * @param {string} data.description - Payment description (optional)
   * @param {string[]} data.payment_methods - Allowed payment methods (default: ['pix','boleto','credit_card'])
   * @param {number} data.installments - Number of installments (default: 1)
   * @param {boolean} data.notify_customer - Send notification (default: true)
   * @returns {Object} Created payment link
   */
  const create = async (data: SubaccountsLinks_CriarInput) => {
    log('usePaymentLinks', 'create', 'Creating payment link', data);
    isLoading.value = true;
    error.value = null;

    try {
      // Validate owner type
      if (data.owner && !VALID_OWNERS.includes(data.owner)) {
        throw new Error(`Tipo de owner invalido: ${data.owner}`);
      }

      validate('SubaccountsLinks_CriarInput', data);
      const response = await httpService.post('/index.php/subaccounts/links/criar', data, { inputContract: 'SubaccountsLinks_CriarInput', responseContract: 'SubaccountsLinks_CriarResponse' });
      log('usePaymentLinks', 'create', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar link de pagamento');
      }
    } catch (err) {
      log('usePaymentLinks', 'create', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List payment links for any owner
   *
   * @param {string} owner - Owner type
   * @param {number} ownerId - Owner ID
   * @returns {Array} Array of payment links
   */
  const listByOwner = async (owner: any, ownerId: any) => {
    log('usePaymentLinks', 'listByOwner', 'Fetching links', { owner, ownerId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/links/lista/${owner}/${ownerId}`, { responseContract: 'SubaccountsLinks_ListaResponse' });
      log('usePaymentLinks', 'listByOwner', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        return [];
      }
    } catch (err) {
      log('usePaymentLinks', 'listByOwner', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get active payment link for any owner
   *
   * @param {string} owner - Owner type
   * @param {number} ownerId - Owner ID
   * @returns {Object|null} Active payment link or null
   */
  const getActiveByOwner = async (owner: any, ownerId: any) => {
    log('usePaymentLinks', 'getActiveByOwner', 'Fetching active link', { owner, ownerId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/links/ativo/${owner}/${ownerId}`, { responseContract: 'SubaccountsLinks_AtivoResponse' });
      log('usePaymentLinks', 'getActiveByOwner', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        return null;
      }
    } catch (err) {
      log('usePaymentLinks', 'getActiveByOwner', 'Error', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get payment link by ID with full details (history, recebimentos)
   *
   * @param {number} linkId - Payment link ID
   * @returns {Object} Payment link with history and recebimentos
   */
  const getById = async (linkId: number) => {
    log('usePaymentLinks', 'getById', 'Fetching link', linkId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/links/detalhe/${linkId}`, { responseContract: 'SubaccountsLinks_DetalheResponse' });
      log('usePaymentLinks', 'getById', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Link nao encontrado');
      }
    } catch (err) {
      log('usePaymentLinks', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get payment link timeline/history
   *
   * @param {number} linkId - Payment link ID
   * @returns {Array} Array of history events
   */
  const getHistory = async (linkId: number) => {
    log('usePaymentLinks', 'getHistory', 'Fetching history', linkId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/links/historico/${linkId}`, { responseContract: 'SubaccountsLinks_HistoricoResponse' });
      log('usePaymentLinks', 'getHistory', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        return [];
      }
    } catch (err) {
      log('usePaymentLinks', 'getHistory', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // BACKWARD COMPATIBLE (COMPROMISSO-SPECIFIC) OPERATIONS
  // ===========================================================================

  /**
   * Create a payment link for a compromisso
   * @param {number} compromissoId - Compromisso ID
   * @param {Object} options - Payment link options
   * @returns {Object} Created payment link with URL
   */
  const createPaymentLink = async (compromissoId: any, options: any = {}) => {
    log('usePaymentLinks', 'createPaymentLink', 'Creating link', { compromissoId, options });
    return create({
      owner: 'compromisso',
      owner_id: compromissoId,
      compromisso_id: compromissoId, // Legacy compatibility
      ...options
    });
  };

  /**
   * Get payment links for a compromisso
   * @param {number} compromissoId - Compromisso ID
   * @returns {Array} Array of payment links
   */
  const getLinksForCompromisso = async (compromissoId: number) => {
    return listByOwner('compromisso', compromissoId);
  };

  /**
   * Get payment link by ID
   * @param {number} linkId - Payment link ID
   * @returns {Object} Payment link details
   */
  const getLink = async (linkId: number) => {
    return getById(linkId);
  };

  /**
   * Check if a compromisso has an active payment link
   * @param {number} compromissoId - Compromisso ID
   * @returns {boolean} True if has active link
   */
  const hasActiveLink = async (compromissoId: number) => {
    const link = await getActiveByOwner('compromisso', compromissoId);
    return link !== null;
  };

  /**
   * Get the active payment link for a compromisso
   * @param {number} compromissoId - Compromisso ID
   * @returns {Object|null} Active payment link or null
   */
  const getActiveLink = async (compromissoId: number) => {
    return getActiveByOwner('compromisso', compromissoId);
  };

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Cancel/deactivate a payment link
   * @param {number} linkId - Payment link ID
   * @returns {Object} Cancellation result
   */
  const deactivateLink = async (linkId: number) => {
    log('usePaymentLinks', 'deactivateLink', 'Deactivating link', linkId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/index.php/subaccounts/links/desativar/${linkId}`, { responseContract: 'SubaccountsLinks_DesativarResponse' });
      log('usePaymentLinks', 'deactivateLink', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao desativar link');
      }
    } catch (err) {
      log('usePaymentLinks', 'deactivateLink', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get PIX QR code for a payment link
   *
   * @param {number} linkId - Payment link ID
   * @returns {Object} PIX data { encodedImage, payload }
   */
  const getPixQrCode = async (linkId: number) => {
    log('usePaymentLinks', 'getPixQrCode', 'Fetching PIX', linkId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/links/pix/${linkId}`, { responseContract: 'SubaccountsLinks_PixResponse' });
      log('usePaymentLinks', 'getPixQrCode', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar QR code PIX');
      }
    } catch (err) {
      log('usePaymentLinks', 'getPixQrCode', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sync ALL charges/payments for a payment link from the provider
   *
   * A payment link can have multiple associated charges (e.g., installments,
   * multiple customers paying through the same link). This function fetches
   * ALL charges from the provider and processes each one.
   *
   * This is useful when:
   * - Webhooks were missed or failed
   * - Manual verification of all payment statuses is needed
   * - Debugging payment flow issues
   *
   * @param {number} linkId - Payment link ID (our internal ID)
   * @returns {Object} Sync result with summary and detailed charge results
   *   - synced: boolean
   *   - payment_link_id: number
   *   - provider_link_id: string
   *   - previous_status: string
   *   - current_status: string
   *   - status_changed: boolean
   *   - summary: { charges_found, charges_processed_ok, charges_with_errors, total_paid, total_pending }
   *   - charges: Array of individual charge results
   *   - errors: Array of errors (if any)
   */
  const syncPaymentLink = async (linkId: number): Promise<SyncResult> => {
    log('usePaymentLinks', 'syncPaymentLink', 'Syncing all charges for payment link', { linkId });
    isLoading.value = true;
    error.value = null;

    try {
      const url = `/index.php/subaccounts/links/sync_payment_link/${linkId}`;
      const response = await httpService.post(url, null, {
        responseContract: 'SubaccountsLinks_SyncPaymentLinkResponse',
        inputContract: 'SubaccountsLinks_SyncPaymentLinkInput'
      });
      log('usePaymentLinks', 'syncPaymentLink', 'Response', response);

      if (response.success) {
        return response.dados as unknown as SyncResult;
      } else {
        throw new Error(response.error || 'Erro ao sincronizar link de pagamento');
      }
    } catch (err) {
      log('usePaymentLinks', 'syncPaymentLink', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sync a SINGLE charge/payment from the provider
   *
   * Use this when you know the specific provider charge ID (e.g., from
   * a recebimento record or from a webhook payload).
   *
   * @param {string} chargeId - Provider charge ID (e.g., "pay_xxx" for ASAAS)
   * @param {number} linkId - Payment link ID (required to identify the payment account)
   * @returns {Object} Sync result for the single charge
   */
  const syncCharge = async (chargeId: string, linkId: number) => {
    log('usePaymentLinks', 'syncCharge', 'Syncing single charge', { chargeId, linkId });
    isLoading.value = true;
    error.value = null;

    try {
      const url = `/index.php/subaccounts/links/sync_charge/${chargeId}?link_id=${linkId}`;
      const response = await httpService.post(url, null, {
        responseContract: 'SubaccountsLinks_SyncChargeResponse',
        inputContract: 'SubaccountsLinks_SyncChargeInput'
      });
      log('usePaymentLinks', 'syncCharge', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao sincronizar cobrança');
      }
    } catch (err) {
      log('usePaymentLinks', 'syncCharge', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Refund a payment made through a payment link (DEPRECATED)
   *
   * @deprecated Use refundByRecebimento() instead for granular control.
   *
   * WARNING: This method uses the payment LINK ID, not the CHARGE ID.
   * A payment link can have multiple charges (e.g., installments).
   * To refund a specific charge, use refundByRecebimento().
   *
   * @param {number} linkId - Payment link ID
   * @param {number} amount - Amount to refund (optional, full refund if not specified)
   * @returns {Object} Refund result
   */
  const refundPayment = async (linkId: any, amount: any = null) => {
    log('usePaymentLinks', 'refundPayment', 'Refunding payment (DEPRECATED - use refundByRecebimento)', { linkId, amount });
    isLoading.value = true;
    error.value = null;

    try {
      const data: SubaccountsLinks_RefundInput = {};
      if (amount !== null) {
        data.value = amount;
      }
      validate('SubaccountsLinks_RefundInput', data);
      const response = await httpService.post(`/index.php/subaccounts/links/refund/${linkId}`, data, { inputContract: 'SubaccountsLinks_RefundInput', responseContract: 'SubaccountsLinks_RefundResponse' });
      log('usePaymentLinks', 'refundPayment', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao estornar pagamento');
      }
    } catch (err) {
      log('usePaymentLinks', 'refundPayment', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Refund a specific recebimento/charge
   *
   * NOTE: The canonical location for this function is in useRecebimento.js
   * as `refundRecebimento()`. This wrapper is kept for backward compatibility.
   *
   * @see useRecebimento.refundRecebimento for full documentation
   *
   * @param {number} recebimentoId - Recebimento ID (from contas_recebimentos table)
   * @param {Object} options - Refund options
   * @param {number} options.value - Amount to refund (optional, full refund if not specified)
   * @param {string} options.description - Reason for the refund (optional)
   * @returns {Object} Refund result with charge_id, provider_response, etc.
   */
  const refundByRecebimento = async (recebimentoId: any, options: any = {}) => {
    log('usePaymentLinks', 'refundByRecebimento', 'Refunding recebimento (wrapper - use useRecebimento.refundRecebimento)', { recebimentoId, options });
    isLoading.value = true;
    error.value = null;

    try {
      const data: SubaccountsLinks_RefundRecebimentoInput = {};
      if (options.value !== undefined && options.value !== null) {
        data.value = options.value;
      }
      if (options.description) {
        data.description = options.description;
      }

      validate('SubaccountsLinks_RefundRecebimentoInput', data);
      const response = await httpService.post(
        `/index.php/subaccounts/links/refund_recebimento/${recebimentoId}`,
        data,
        { inputContract: 'SubaccountsLinks_RefundRecebimentoInput', responseContract: 'SubaccountsLinks_RefundRecebimentoResponse' }
      );
      log('usePaymentLinks', 'refundByRecebimento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao estornar pagamento');
      }
    } catch (err) {
      log('usePaymentLinks', 'refundByRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // STATISTICS & DASHBOARD
  // ===========================================================================

  /**
   * Fetch all payment links for current franchise (dashboard view)
   *
   * @param {Object} filters - Optional filters
   * @param {string} filters.status - Filter by status
   * @param {string} filters.date_from - Start date (YYYY-MM-DD)
   * @param {string} filters.date_to - End date (YYYY-MM-DD)
   * @returns {Array} Array of payment links
   */
  const fetchPaymentLinks = async (filters: any = {}): Promise<PaymentLinkRow[]> => {
    log('usePaymentLinks', 'fetchPaymentLinks', 'Fetching all links', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const queryString = params.toString();
      const url = `/index.php/subaccounts/links/todos${queryString ? '?' + queryString : ''}`;

      const response = await httpService.get(url, { responseContract: 'SubaccountsLinks_TodosResponse' });
      log('usePaymentLinks', 'fetchPaymentLinks', 'Response', response);

      if (response.success) {
        return (response.dados || []) as unknown as PaymentLinkRow[];
      } else {
        throw new Error(response.error || 'Erro ao buscar links de pagamento');
      }
    } catch (err) {
      log('usePaymentLinks', 'fetchPaymentLinks', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get payment statistics for dashboard
   *
   * @param {Object} filters - Optional filters
   * @param {string} filters.start_date - Start date (YYYY-MM-DD)
   * @param {string} filters.end_date - End date (YYYY-MM-DD)
   * @returns {Object} Statistics object
   */
  const getStats = async (filters: any = {}) => {
    log('usePaymentLinks', 'getStats', 'Fetching stats', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const queryString = params.toString();
      const url = `/index.php/subaccounts/links/stats${queryString ? '?' + queryString : ''}`;

      const response = await httpService.get(url, { responseContract: 'SubaccountsLinks_StatsResponse' });
      log('usePaymentLinks', 'getStats', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar estatisticas');
      }
    } catch (err) {
      log('usePaymentLinks', 'getStats', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // UTILITY FUNCTIONS
  // ===========================================================================

  /**
   * Copy payment link URL to clipboard
   * @param {string} url - Payment link URL
   * @returns {boolean} Success status
   */
  const copyLinkToClipboard = async (url: any) => {
    try {
      await navigator.clipboard.writeText(url);
      log('usePaymentLinks', 'copyLinkToClipboard', 'URL copied', url);
      return true;
    } catch (err) {
      log('usePaymentLinks', 'copyLinkToClipboard', 'Error', err);
      return false;
    }
  };

  /**
   * Open payment link in new tab
   * @param {string} url - Payment link URL
   */
  const openLinkInNewTab = (url: any) => {
    window.open(url, '_blank');
  };

  /**
   * Get link status display configuration
   * @param {string} status - Link status
   * @returns {Object} Status config with label, severity, icon
   */
  const getLinkStatusConfig = (status: any) => {
    const configs = {
      'active': { label: 'Ativo', severity: 'info', icon: 'pi-link' },
      'paid': { label: 'Pago', severity: 'success', icon: 'pi-check-circle' },
      'partially_paid': { label: 'Parcialmente Pago', severity: 'warn', icon: 'pi-chart-pie' },
      'expired': { label: 'Expirado', severity: 'danger', icon: 'pi-clock' },
      'overdue': { label: 'Vencido', severity: 'danger', icon: 'pi-calendar-times' },
      'cancelled': { label: 'Cancelado', severity: 'secondary', icon: 'pi-times' },
      'refunded': { label: 'Estornado', severity: 'warn', icon: 'pi-replay' }
    };
    return (configs as any)[status] || { label: status, severity: 'secondary', icon: 'pi-question' };
  };

  /**
   * Format payment value for display (handles both cents and reais)
   * @param {number} value - Value (in cents if > 100, otherwise in reais)
   * @param {boolean} isCents - Whether value is in cents
   * @returns {string} Formatted value
   */
  const formatValue = (value: any, isCents = false) => {
    const amount = isCents ? value / 100 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount || 0);
  };

  /**
   * Format amount from cents to currency string
   *
   * @param {number} amountCents - Amount in cents
   * @returns {string} Formatted amount (e.g., "R$ 150,00")
   */
  const formatAmountCents = (amountCents: any) => {
    return formatValue(amountCents, true);
  };

  /**
   * Check if payment link can be cancelled
   *
   * @param {Object} link - Payment link object
   * @returns {boolean} True if can be cancelled
   */
  const canCancel = (link: any) => {
    return ['active', 'overdue'].includes(link?.status);
  };

  /**
   * Check if payment link can be refunded
   *
   * @param {Object} link - Payment link object
   * @returns {boolean} True if can be refunded
   */
  const canRefund = (link: any) => {
    return link?.status === 'paid';
  };

  return {
    // State
    isLoading,
    error,

    // Constants
    VALID_OWNERS,
    STATUS,

    // Polymorphic CRUD operations
    create,
    listByOwner,
    getActiveByOwner,
    getById,
    getHistory,

    // Backward compatible (compromisso-specific) operations
    createPaymentLink,
    getLinksForCompromisso,
    getLink,
    hasActiveLink,
    getActiveLink,

    // Actions
    deactivateLink,
    getPixQrCode,
    refundPayment,        // DEPRECATED: Use useRecebimento.refundRecebimento instead
    refundByRecebimento,  // Wrapper for backward compatibility - canonical: useRecebimento.refundRecebimento
    syncPaymentLink,
    syncCharge,

    // Statistics & Dashboard
    fetchPaymentLinks,
    getStats,

    // Utility functions
    copyLinkToClipboard,
    openLinkInNewTab,
    getLinkStatusConfig,
    formatValue,
    formatAmountCents,
    canCancel,
    canRefund,
  };
}
